import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { TrendingUp, MousePointerClick, Activity, GitMerge, Link2, ArrowRight, RefreshCw } from 'lucide-react';

const CAPABILITIES = [
  { icon: TrendingUp, title: 'Page-2 Keyword Rescue', desc: 'We pinpoint the keywords you already rank #11–20 for and push them onto page 1 — the fastest wins in SEO.' },
  { icon: MousePointerClick, title: 'Click-Through Optimizer', desc: 'Pages that show up but don\'t get clicked are leaking traffic. We rewrite titles with AI to win the click.' },
  { icon: Activity, title: 'Ranking Decay Radar', desc: 'We catch pages quietly losing traffic and fix them before they fall off Google — not months later.' },
  { icon: GitMerge, title: 'Cannibalization Cleanup', desc: 'When two of your pages fight for the same search, both lose. We find them and consolidate the authority.' },
  { icon: Link2, title: 'Authority Routing', desc: 'We map internal links to send your strongest pages\' authority exactly where it lifts rankings most.' },
];

/**
 * Reusable "Always-On SEO Intelligence" marketing showcase.
 * On-palette: blue / slate / white only.
 *  - compact: condensed version for /packages, /pricing, proposals (links out)
 *  - full:    used inside the dedicated landing page + homepage section
 */
export default function SEOIntelligenceShowcase({ compact = false, showCta = true }) {
  return (
    <div className={compact ? '' : 'max-w-6xl mx-auto'} data-testid="seo-intel-showcase">
      <div className="text-center mb-8">
        <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide uppercase text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-3 py-1">
          <RefreshCw className="h-3.5 w-3.5" /> Always-On SEO Intelligence
        </span>
        <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900">
          We don't just build your site. <span className="text-blue-600">We grow it — every week.</span>
        </h2>
        <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">
          Our AI reads your Google Search Console data every week and turns it into a prioritized list of ranking
          opportunities — then we act on them. Most agencies build a site and walk away. We keep climbing.
        </p>
      </div>

      <div className={`grid gap-4 ${compact ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
        {(compact ? CAPABILITIES.slice(0, 4) : CAPABILITIES).map((c, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-blue-400 hover:shadow-lg transition-all duration-200"
            data-testid={`seo-cap-${i}`}>
            <div className="h-10 w-10 rounded-lg bg-blue-600/10 flex items-center justify-center mb-3">
              <c.icon className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-base font-semibold text-slate-900">{c.title}</h3>
            <p className="mt-1.5 text-sm text-slate-600">{c.desc}</p>
          </div>
        ))}
        {!compact && (
          <div className="bg-gradient-to-b from-blue-900 to-slate-800 rounded-xl border border-blue-500 p-5 flex flex-col justify-center text-white">
            <h3 className="text-base font-semibold">Weekly intelligence report</h3>
            <p className="mt-1.5 text-sm text-blue-200">One email, every site you own, ranked by opportunity. You always know exactly what's moving — and what we're fixing next.</p>
          </div>
        )}
      </div>

      {showCta && (
        <div className="mt-8 text-center">
          <Link to="/seo-intelligence">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold h-12 px-7 text-base" data-testid="seo-intel-cta">
              See how it works & get a free scan <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
